import React, { useEffect, useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import {
  fetchCertificateTypes, fetchInstructorExternalIds, fetchTenantInstructorIds, saveInstructorExternalId,
} from "../../api/apiClient";

const AHA_ID = /^[0-9]{1,11}$/;

/**
 * An instructor's AHA instructor ID for this training center, shown when the center has AHA eCards set up.
 * Coordinators and admins add it, change it (AHA can issue a new one) or clear it. Without an ID the instructor can't
 * be assigned to AHA classes.
 */
export default function InstructorAhaIdField({ userId, canEdit }) {
  const [state, setState] = useState({ loading: true, ahaSetUp: false, value: "", onFile: false });
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const types = await fetchCertificateTypes();
        const ahaSetUp = (types || []).some((t) => t.type === "AHA" && t.enabled);
        if (!ahaSetUp) {
          if (active) setState({ loading: false, ahaSetUp: false, value: "", onFile: false });
          return;
        }
        let value = "";
        let onFile = false;
        if (canEdit) {
          const mine = await fetchInstructorExternalIds(userId);
          value = mine?.ids?.AHA_INSTRUCTOR || "";
          onFile = value !== "";
        } else {
          const all = await fetchTenantInstructorIds();
          onFile = Object.prototype.hasOwnProperty.call(all?.ids?.AHA_INSTRUCTOR || {}, String(userId));
        }
        if (active) {
          setState({ loading: false, ahaSetUp: true, value, onFile });
          setDraft(value);
        }
      } catch {
        if (active) setState({ loading: false, ahaSetUp: false, value: "", onFile: false, error: true });
      }
    })();
    return () => { active = false; };
  }, [userId, canEdit]);

  if (state.loading) return null;
  if (state.error) return <Alert severity="warning" sx={{ my: 1 }}>The AHA instructor ID couldn't be loaded.</Alert>;
  if (!state.ahaSetUp) return null;

  const trimmed = draft.trim();
  const invalid = trimmed !== "" && !AHA_ID.test(trimmed);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await saveInstructorExternalId(userId, "AHA_INSTRUCTOR", trimmed);
      const value = saved?.ids?.AHA_INSTRUCTOR || "";
      setState((s) => ({ ...s, value, onFile: value !== "" }));
      setDraft(value);
      setMessage({ severity: "success", text: !value ? "AHA instructor ID removed."
        : state.value ? "AHA instructor ID updated." : "AHA instructor ID saved." });
    } catch (e) {
      setMessage({ severity: "error", text: e?.response?.data?.message || "The AHA instructor ID couldn't be saved." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="instructor-aha-id" sx={{ p: 2, my: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
      <Typography sx={{ fontWeight: 700 }}>AHA instructor ID</Typography>
      {message && <Alert severity={message.severity} sx={{ my: 1 }}>{message.text}</Alert>}
      {canEdit ? (
        <>
          <TextField label="AHA instructor ID" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={saving}
            margin="dense" inputProps={{ maxLength: 11, inputMode: "numeric" }} error={invalid}
            helperText={invalid ? "Digits only, up to 11."
              : state.value
                ? "On file. Change it here if AHA issued a new ID; leave blank to remove it. Leading zeros are kept."
                : "Needed to teach AHA classes at this center. Leading zeros are kept."} />
          <Box>
            <Button size="small" variant="contained" onClick={save} disabled={saving || invalid || trimmed === state.value}>
              {state.value && trimmed ? "Update AHA instructor ID" : "Save AHA instructor ID"}
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color={state.onFile ? "text.primary" : "text.secondary"}>
          {state.onFile
            ? "On file. This instructor can teach AHA classes."
            : "Not on file. A coordinator or administrator must add it before this instructor can teach AHA classes."}
        </Typography>
      )}
    </Box>
  );
}
